"use client";

import { AppBar, Card, Tab } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import Tabs from "@/components/atoms/ZoomAwareTabs";
import MDBox from "@/components/atoms/MDBox";
import AlertBlock from "@/components/molecules/AlertBlock";
import { CustomerTabs } from "@/components/screens/CustomerFormPage/types";
import useTabs from "@/hooks/use-tabs";
import ICustomer from "@/interfaces/customer.interfaces";
import { CustomerMessages } from "@/messages/customer/customer.messages";

import ConfigurationTab from "./components/ConfigurationTab";
import GeneralInformationTab from "./components/GeneralInformationTab";
import PlanManagementTab from "./components/PlanManagementTab";
import { CUSTOMER_TABS_ITEMS } from "./constants";

type Props = {
  customer: ICustomer | null;
  messages: CustomerMessages;
  error?: Error;
  isLoading: boolean;
};

const SUBSCRIPTION_TAB: CustomerTabs = "subscriptionManagementTab";

function CustomerForm({ customer, messages, error, isLoading }: Props) {
  const [searchParams] = useSearchParams();
  const TABS = CUSTOMER_TABS_ITEMS(messages);
  const initialTab = useMemo<CustomerTabs>(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === SUBSCRIPTION_TAB) {
      return SUBSCRIPTION_TAB;
    }
    return "generalInformationTab";
  }, [searchParams]);
  const tabs = useTabs<CustomerTabs>({ initialTab, tabs: TABS });
  const { activeTab, changeTab, tabsOrientation } = tabs;

  // Sincroniza apenas quando a URL pede explicitamente a aba de assinatura.
  // Nao resetar para generalInformationTab a cada re-render (quebrava clique manual na aba).
  useEffect(() => {
    if (searchParams.get("tab") === SUBSCRIPTION_TAB) {
      changeTab(SUBSCRIPTION_TAB);
    }
  }, [searchParams, changeTab]);

  const isInfoTab = activeTab === "generalInformationTab";
  const isSubManTab = activeTab === "subscriptionManagementTab" && customer;
  const isConfigurationTab = activeTab === "configurationTab" && customer;

  const shouldLoadError = !customer && !!error && !isLoading;

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 12, md: "auto", lg: "auto" }}>
          <AppBar position="static">
            <Tabs
              orientation={tabsOrientation}
              value={activeTab}
              onChange={(_, newTab) => changeTab(newTab)}
              sx={{ width: "100%" }}
            >
              {TABS.map((tab, index) => (
                <Tab sx={{ px: 2 }} key={index} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
          </AppBar>
        </Grid>
      </Grid>

      {isInfoTab && <GeneralInformationTab customer={customer} messages={messages} isLoading={isLoading} />}
      {isSubManTab && <PlanManagementTab messages={messages} />}
      {isConfigurationTab && <ConfigurationTab messages={messages} />}

      {!!shouldLoadError && (
        <MDBox mt={2}>
          <Card>
            <AlertBlock
              title={messages.errorToLoad}
              description={error.message}
              alt={messages.notFoundAlt}
            />
          </Card>
        </MDBox>
      )}
    </>
  );
}

export default CustomerForm;
